import { useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompanyPageStore } from "../../../store/companyPageStore";
import { CompanyTab, TAB_DEFINITIONS } from "../types";
import { useSnackbar } from "../../../components/Snackbar";

const getTabFromPath = (pathname: string): CompanyTab => {
  if (pathname.endsWith("/spend")) return CompanyTab.SPEND;
  if (pathname.endsWith("/install")) return CompanyTab.INSTALL;
  if (pathname.endsWith("/contract")) return CompanyTab.CONTRACT;
  if (pathname.endsWith("/intent")) return CompanyTab.INTENT;

  return CompanyTab.SUMMARY;
};

const getPathFromTab = (
  tabIndex: CompanyTab,
  companyId: string,
  search = ""
): string => {
  const basePath = `/catalog/companies/${companyId}`;
  return `${basePath}${TAB_DEFINITIONS[tabIndex].path}${search}`;
};

const MESSAGES: Partial<Record<CompanyTab, string>> = {
  [CompanyTab.SPEND]: "No Spend information for this company at this time",
  [CompanyTab.INSTALL]: "No Install information for this company at this time",
};

export const useCompanyTabs = (
  companyId: string,
  isDrawer: boolean = false
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showInfo } = useSnackbar();
  const hasSpendData = useCompanyPageStore((state) => state.hasSpendData);
  const hasInstallData = useCompanyPageStore((state) => state.hasInstallData);
  const company = useCompanyPageStore((state) => state.company);
  const activeTab = useCompanyPageStore((state) => state.activeTab);
  const setActiveTab = useCompanyPageStore((state) => state.setActiveTab);
  const isLoadingSpendData = useCompanyPageStore(
    (state) => state.loadingStates.spendData
  );
  const isLoadingInstallData = useCompanyPageStore(
    (state) => state.loadingStates.installData
  );

  // Check if a tab has data available
  const isTabAvailable = useCallback(
    (tab: CompanyTab): boolean => {
      if (!company?.hgId || isLoadingSpendData || isLoadingInstallData) {
        return true; // Allow tab access while loading
      }

      switch (tab) {
        case CompanyTab.SPEND:
          return hasSpendData;
        case CompanyTab.INSTALL:
          return hasInstallData;
        default:
          return true; // SUMMARY and CONTRACT are always available
      }
    },
    [
      company?.hgId,
      hasSpendData,
      hasInstallData,
      isLoadingSpendData,
      isLoadingInstallData,
    ]
  );

  // Handle tab change with validation
  const handleInvalidTab = useCallback(
    (tab: CompanyTab) => {
      const message = MESSAGES[tab];
      if (message) {
        showInfo(message);
      }
      setActiveTab(CompanyTab.SUMMARY);
    },
    [showInfo, setActiveTab]
  );

  // Handle URL-based tab sync for non-drawer mode
  useEffect(() => {
    if (isDrawer) return;

    const currentTab = getTabFromPath(location.pathname);

    if (!isTabAvailable(currentTab)) {
      handleInvalidTab(currentTab);
      navigate(getPathFromTab(CompanyTab.SUMMARY, companyId, location.search), {
        replace: true,
      });
    } else {
      setActiveTab(currentTab);
    }
  }, [
    location.pathname,
    location.search,
    companyId,
    navigate,
    setActiveTab,
    isDrawer,
    isTabAvailable,
    handleInvalidTab,
  ]);

  // Handle tab validation for drawer mode (no URL sync)
  useEffect(() => {
    if (!isDrawer) return;

    if (!isTabAvailable(activeTab)) {
      handleInvalidTab(activeTab);
    }
  }, [activeTab, isDrawer, isTabAvailable, handleInvalidTab]);

  const handleTabChange = (
    _event?: React.SyntheticEvent,
    newValue?: number
  ) => {
    if (newValue === undefined) return;

    const tab = newValue as CompanyTab;

    if (!isTabAvailable(tab)) {
      handleInvalidTab(tab);
      return;
    }

    setActiveTab(tab);

    // Only sync with URL if not in drawer mode
    if (!isDrawer) {
      navigate(getPathFromTab(tab, companyId, location.search));
    }
  };

  return { activeTab, handleTabChange };
};
